import { FullCalendarWithEventPortals } from "@/components/shared/full-calendar-with-event-portals"
import { EventContentArg, EventMountArg } from "@fullcalendar/core/index.js"
import enAuLocale from "@fullcalendar/core/locales/en-au"
import scrollGridPlugin from "@fullcalendar/scrollgrid"
import interactionPlugin from "@fullcalendar/interaction"
import timeGridPlugin from "@fullcalendar/timegrid"
import { useCallback, useMemo } from "react"
import { computeFullCalendarEvents } from "@/lib/fullCalendar"
import { CycleEvent } from "@/types/cycle-event"

type TypedEventContentArg<TExtendedProps> = Omit<EventContentArg, "event"> & {
  event: Omit<EventContentArg["event"], "extendedProps"> & {
    extendedProps: TExtendedProps
  }
}

type CycleCalendarProps<TExtendedProps extends Record<string, unknown>> = Omit<
  Partial<React.ComponentPropsWithoutRef<typeof FullCalendarWithEventPortals>>,
  "calendarRef" | "events" | "portalEventContent"
> &
  Pick<
    React.ComponentPropsWithoutRef<typeof FullCalendarWithEventPortals>,
    "calendarRef"
  > & {
    cycleLength: number
    interactive: boolean
    calendarTimezoneId: string
    calendarStartInstant: Temporal.Instant
    events: Array<CycleEvent<TExtendedProps>>
    eventContent: (
      eventContentArg: TypedEventContentArg<TExtendedProps>,
    ) => React.ReactNode
  }

export function CycleCalendar<TExtendedProps extends Record<string, unknown>>({
  cycleLength,
  eventDidMount,
  interactive,
  calendarTimezoneId,
  calendarStartInstant,
  events,
  eventContent,
  ...props
}: CycleCalendarProps<TExtendedProps>) {
  const handleEventMount = useCallback(
    (info: EventMountArg) => {
      const { el, event } = info

      el.classList.add("cursor-default!")
      const { bgColour, textColour, eventBorderColour } =
        event.extendedProps.colours
      el.classList.add(bgColour)
      el.classList.add(eventBorderColour)

      el.querySelector(".fc-event-main")!.classList.add(textColour)
      el.querySelector(".fc-event-main")!.classList.add(bgColour)

      eventDidMount?.(info)
    },
    [eventDidMount],
  )

  const plugins = [timeGridPlugin, scrollGridPlugin]
  if (interactive) {
    plugins.push(interactionPlugin)
  }

  const fullCalendarEvents = useMemo(
    () =>
      events.flatMap(
        ({
          extendedProps,
          startTime,
          day,
          duration,
          eventTitle,
          cycleLength,
          eventTimezoneId,
        }) =>
          computeFullCalendarEvents({
            eventTitle,
            startTime,
            duration: duration,
            day,
            calendarTimezoneId,
            extendedProps,
            cycleLength,
            eventTimezoneId,
            startTemporalZonedDateTime:
              calendarStartInstant.toZonedDateTimeISO("UTC"),
          }),
      ),
    [events, calendarStartInstant, calendarTimezoneId],
  )

  return (
    <FullCalendarWithEventPortals
      events={fullCalendarEvents}
      portalEventContent={
        eventContent as (arg: EventContentArg) => React.ReactNode
      }
      timeZone="UTC"
      locale={enAuLocale}
      plugins={plugins}
      initialView="timeGridWeek"
      headerToolbar={false}
      dayMinWidth={80}
      expandRows={true}
      height={"100%"}
      scrollTimeReset={false}
      stickyHeaderDates={true}
      eventStartEditable={true}
      editable={false}
      droppable={true}
      allDaySlot={false}
      weekends={true}
      dayHeaderFormat={{ weekday: "short" }}
      views={{
        timeGridWeek: {
          type: "timeGrid",
          duration: { weeks: cycleLength! / 7 },
          titleFormat: { week: "short" },
        },
      }}
      slotDuration="01:00:00"
      slotLabelInterval="04:00:00"
      now={calendarStartInstant.toString()}
      eventDidMount={handleEventMount}
      mirrorEventContent={({ event }) => (
        <div className="fc-event-main-frame">
          <div className="fc-event-title fc-sticky top-0 text-xs">
            {event.title}
          </div>
        </div>
      )}
      {...props}
    />
  )
}
